import React, { useState } from 'react';
import { usePayouts, useAssets } from '../../hooks/useInvestQueries';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';
import AppLayout from '../../components/layouts/AppLayout';
import ResponsiveNavbar from '../../components/ResponsiveNavbar';
import { DollarSign, Plus, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PaymentDetailsDialog from '../../components/ui/PaymentDetailsDialog';

export default function AdminPayouts() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: payouts = [], isLoading: loadingPayouts, error: errorPayouts } = usePayouts();
  const { data: assets = [], isLoading: loadingAssets, error: errorAssets } = useAssets();
  const loading = loadingPayouts || loadingAssets;
  
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitForm, setExitForm] = useState({ asset_id: '', final_sale_amount: '', exit_date: '', exit_type: 'full_sale', notes: '' });

  if (errorPayouts || errorAssets) {
    toast.error('Failed to load data');
  }

  const [selectedPayoutId, setSelectedPayoutId] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);

  const handleCreateExit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/exits', { ...exitForm, exit_date: new Date(exitForm.exit_date).toISOString() });
      toast.success('Asset exit processed! Payouts created.');
      setShowExitDialog(false);
      setExitForm({ asset_id: '', final_sale_amount: '', exit_date: '', exit_type: 'full_sale', notes: '' });
      queryClient.invalidateQueries(['payouts']);
      queryClient.invalidateQueries(['assets']); // Maybe asset status changed
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exit creation failed');
    }
  };

  const openMarkPaidDialog = (payoutId) => {
    setSelectedPayoutId(payoutId);
    setShowPaymentDialog(true);
  };

  const handleMarkPaidConfirm = async ({ method, reference }) => {
    try {
      await api.post(`/payouts/${selectedPayoutId}/mark-paid`, null, { 
        params: { payment_method: method, transaction_ref: reference } 
      });
      toast.success('Payout marked as paid');
      queryClient.invalidateQueries(['payouts']);
      queryClient.invalidateQueries(['admin-stats']);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    }
  };

  const claimSubmitted = payouts.filter(p => p.claim_status === 'claim_submitted');
  const paid = payouts.filter(p => p.claim_status === 'paid');

  return (
    <AppLayout>
      <div className="min-h-screen" data-testid="admin-payouts-page">
        {/* Responsive Navigation */}
        <ResponsiveNavbar isAdmin={true} />

        {/* Content */}
        <div className="container-responsive max-w-7xl mx-auto py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h1 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold">Payout Management</h1>
            <button onClick={() => setShowExitDialog(true)} className="w-full sm:w-auto px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all flex items-center justify-center gap-2" data-testid="create-exit-btn">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Mark Asset Exit</span>
            </button>
          </div>

          {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37] mx-auto"></div></div>
        ) : (
          <div className="space-y-8">
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-[#F59E0B]" />
                Pending Payout Claims ({claimSubmitted.length})
              </h2>
              {claimSubmitted.length === 0 ? (
                <div className="premium-card p-8 text-center"><p className="text-[#64748B]">No pending payout claims</p></div>
              ) : (
                <div className="space-y-4">
                  {claimSubmitted.map(payout => (
                    <div key={payout.payout_id} className="premium-card p-6" data-testid={`payout-${payout.payout_id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-['Outfit'] text-xl font-bold mb-2">{payout.asset_name}</h3>
                          <p className="text-[#94A3B8] mb-1">{payout.investor_name} ({payout.investor_email})</p>
                          <div className="flex gap-4 text-sm mb-2">
                            <span className="text-[#64748B]">Ownership: <span className="text-white font-['JetBrains_Mono']">{payout.percentage_owned}%</span></span>
                            <span className="text-[#64748B]">Claimed: {new Date(payout.claim_date).toLocaleDateString()}</span>
                          </div>
                          <p className="font-['JetBrains_Mono'] text-2xl font-bold text-[#D4AF37]">${payout.payout_amount.toLocaleString()}</p>
                        </div>
                        <button onClick={() => openMarkPaidDialog(payout.payout_id)} className="px-6 py-3 bg-[#10B981] text-white font-bold rounded-lg hover:bg-[#059669] transition-colors" data-testid={`mark-paid-btn-${payout.payout_id}`}>
                          Mark as Paid
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-[#10B981]" />
                Paid Payouts ({paid.length})
              </h2>
              {paid.length === 0 ? (
                <div className="premium-card p-8 text-center"><p className="text-[#64748B]">No paid payouts yet</p></div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paid.map(payout => (
                    <div key={payout.payout_id} className="premium-card p-4" data-testid={`paid-payout-${payout.payout_id}`}>
                      <h4 className="font-['Outfit'] font-bold mb-2">{payout.asset_name}</h4>
                      <p className="text-[#94A3B8] text-sm mb-2">{payout.investor_name}</p>
                      <p className="font-['JetBrains_Mono'] text-lg font-bold text-[#10B981] mb-1">${payout.payout_amount.toLocaleString()}</p>
                      <p className="text-[#64748B] text-xs">Paid: {new Date(payout.payment_date).toLocaleDateString()}</p>
                      {payout.payment_method && <p className="text-[#64748B] text-xs">{payout.payment_method}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      </div>

      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-bold">Mark Asset Exit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateExit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-2">Select Asset *</label>
              <select 
                value={exitForm.asset_id} 
                onChange={(e) => setExitForm({ ...exitForm, asset_id: e.target.value })} 
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none [&>option]:text-black" 
                required 
                data-testid="exit-asset-select"
              >
                <option value="">Choose an asset...</option>
                {assets.filter(a => a.percent_committed > 0).map(asset => (
                  <option key={asset.asset_id} value={asset.asset_id}>{asset.name} ({asset.percent_committed}% committed)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Final Sale Amount ($) *</label>
              <input type="number" value={exitForm.final_sale_amount} onChange={(e) => setExitForm({ ...exitForm, final_sale_amount: Number(e.target.value) })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="exit-amount-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Exit Date *</label>
              <input type="date" value={exitForm.exit_date} onChange={(e) => setExitForm({ ...exitForm, exit_date: e.target.value })} className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" required data-testid="exit-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Exit Type</label>
              <select 
                value={exitForm.exit_type} 
                onChange={(e) => setExitForm({ ...exitForm, exit_type: e.target.value })} 
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none [&>option]:text-black" 
                data-testid="exit-type-select"
              >
                <option value="full_sale">Full Sale</option>
                <option value="partial_exit">Partial Exit</option>
                <option value="refinance">Refinance</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea value={exitForm.notes} onChange={(e) => setExitForm({ ...exitForm, notes: e.target.value })} rows="2" className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none" data-testid="exit-notes-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setShowExitDialog(false)} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">Cancel</button>
              <button type="submit" className="flex-1 py-3 bg-[#D4AF37] text-[#0A1128] font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all" data-testid="submit-exit-btn">Process Exit</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      <PaymentDetailsDialog 
        isOpen={showPaymentDialog} 
        onClose={() => setShowPaymentDialog(false)} 
        onSubmit={handleMarkPaidConfirm}
        title="Mark Payout as Paid"
        submitLabel="Mark Paid"
      />
    </AppLayout>
  );
}
